import { getCurrentSlug } from './slugManager';
import type { Place, PlaceTranslation, FirestorePlaceDoc, GeoPoint, StationLink } from '../types';

/**
 * Resolve the best matching language key from a multilingual map.
 */
export function resolveLang(nameMap: Record<string, unknown>, langCode: string | null): string {
  if (!langCode) return Object.keys(nameMap)[0] || 'en';
  if (nameMap[langCode]) return langCode;
  const base = langCode.split('-')[0];
  if (nameMap[base]) return base;
  for (const key of Object.keys(nameMap)) {
    if (key.startsWith(base)) return key;
  }
  return Object.keys(nameMap)[0] || langCode;
}

/**
 * Normalise an asset reference: full URLs pass through, bare IDs stay as-is.
 */
export function extractAssetId(urlStr: string | null | undefined): string | null {
  if (!urlStr) return null;
  return urlStr;
}

/**
 * Check whether a Firestore place document has content in the given language.
 * Used to hide items from listings when no translation exists for the active language.
 * Returns true for documents with an empty or missing name map (backward-compatible).
 */
export function hasLanguageContent(fsDoc: FirestorePlaceDoc, langCode: string): boolean {
  if (!fsDoc.name || Object.keys(fsDoc.name).length === 0) return true;
  const base = langCode.split('-')[0];
  return Object.keys(fsDoc.name).some(
    key => key === langCode || key === base || key.startsWith(base)
  );
}

/**
 * Convert a Firestore document to the Place shape the UI expects.
 */
export function toPlace(fsDoc: FirestorePlaceDoc, langCode: string): Place | null {
  if (!fsDoc) return null;
  const lang = resolveLang(fsDoc.name || {}, langCode);

  const translations: PlaceTranslation[] = [{
    languages_code: langCode,
    information_title: fsDoc.name?.[lang] || '',
    information_subtitle: fsDoc.subtitle?.[lang] || '',
    information_description: fsDoc.description?.[lang] || '',
    information_short_description: fsDoc.shortDescription?.[lang] || '',
    information_address: fsDoc.address || '',
    information_date: fsDoc.dateRange || '',
    audio: null,
    video: null,
    accessibility_hearing_video_subtitles: null,
    links: fsDoc.links
      ? (fsDoc.links[resolveLang(fsDoc.links, langCode)] || []) as StationLink[]
      : [],
  }];

  const location: GeoPoint | null = fsDoc.location
    ? { type: 'Point', coordinates: [fsDoc.location.lng, fsDoc.location.lat] }
    : null;

  return {
    id: parseInt(fsDoc.id, 10) || fsDoc.id,
    parent_id: fsDoc.parentId ? (parseInt(fsDoc.parentId, 10) || fsDoc.parentId) : (fsDoc.spaceId ? (parseInt(fsDoc.spaceId, 10) || fsDoc.spaceId) : null),
    translations,
    information_photo: extractAssetId(fsDoc.coverImage) || null,
    information_subtitle: fsDoc.subtitle || null,
    information_icon: fsDoc.iconUrl ? { id: extractAssetId(fsDoc.iconUrl)!, title: fsDoc.stationType === 'panoramic' ? 'V360' : '' } : null,
    information_photos: (fsDoc.gallery || []).map(u => ({ fileId: extractAssetId(u) })),
    information_photo_type: fsDoc.isPanoramic ? 'Panoramic' : 'Standard',
    thumbnail_type: fsDoc.displayMode === 'minor' ? 'minor' : null,
    keypad_id: fsDoc.identifiers?.displayNumber || null,
    type: fsDoc.stationType || fsDoc.spaceType || 'station',
    children_layout: fsDoc.spaceType || null,
    children_list: fsDoc.children_list || [],
    parent_list_order: fsDoc.order ?? 0,
    sort: fsDoc.order ?? 0,
    status: fsDoc.status || 'published',
    location,
    tour_duration: fsDoc.tourDuration || null,
    tour_distance: fsDoc.tourDistance || null,
    information_title: fsDoc.name?.[lang] || '',
    slug: fsDoc.slug || getCurrentSlug() || '',
    _firestoreDoc: fsDoc,
  };
}
